import { ref, onUnmounted, watch, type Ref } from 'vue';

type FormatType = 'colon' | 'labelled';

function pad(n: number) {
  return n.toString().padStart(2, '0');
}

export function formatTimeRemaining(
  end_time: number,
  format: FormatType = 'colon',
) {
  const now = Date.now();
  let diff = end_time - now;

  if (diff <= 0) return format === 'colon' ? '00:00:00:00' : '00D 00:00:00';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  diff -= days * 1000 * 60 * 60 * 24;

  const hours = Math.floor(diff / (1000 * 60 * 60));

  diff -= hours * 1000 * 60 * 60;

  const minutes = Math.floor(diff / (1000 * 60));

  diff -= minutes * 1000 * 60;

  const seconds = Math.floor(diff / 1000);

  if (format === 'labelled') {
    return `${days}D ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  return `${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export function useTournamentTimer(
  endTime: number | Ref<number>,
  options?: { autoStart?: boolean; format?: FormatType; tickMs?: number },
) {
  const remaining = ref('');
  let intervalId: ReturnType<typeof setInterval> | undefined;

  const format = options?.format ?? 'colon';
  const tick = options?.tickMs ?? 1000;

  function update() {
    const value = typeof endTime === 'number' ? endTime : endTime.value;

    remaining.value = formatTimeRemaining(value ?? 0, format);
  }

  function start() {
    if (intervalId) return;
    update();
    intervalId = setInterval(() => update(), tick);
  }

  function stop() {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = undefined;
    }
  }

  if (options?.autoStart ?? true) start();

  if (typeof endTime !== 'number') {
    watch(endTime as Ref<number>, () => {
      update();
    });
  }

  onUnmounted(() => stop());

  return {
    remaining,
    start,
    stop,
    formatTimeRemaining,
  };
}

export default useTournamentTimer;
